import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Alert, Spinner, Card, Accordion, Button, Modal, Form } from 'react-bootstrap';
import api from '../api/axios';
import { useLanguage } from '../contexts/LanguageContext';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendModal, setResendModal] = useState({ show: false, orderId: null, emails: '', sending: false, feedback: '' });
  const { t } = useLanguage();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/orders');
      setOrders(response.data?.orders || []);
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      confirmed: 'info',
      processing: 'primary',
      shipped: 'success',
      delivered: 'secondary',
      cancelled: 'danger'
    };
    
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const handleDownloadPDF = (pdfUrl, orderNumber) => {
    // Open PDF in new tab for viewing/downloading
    if (pdfUrl) {
      // If it's a relative URL, prepend the API base URL
      const fullUrl = pdfUrl.startsWith('http') 
        ? pdfUrl 
        : `${process.env.REACT_APP_API_URL || 'http://localhost:7000'}${pdfUrl}`;
      window.open(fullUrl, '_blank');
    }
  };

  const openResendModal = (orderId) => {
    setResendModal({ show: true, orderId, emails: '', sending: false, feedback: '' });
  };

  const closeResendModal = () => {
    setResendModal({ show: false, orderId: null, emails: '', sending: false, feedback: '' });
  };

  const handleResendEmail = async () => {
    const emails = resendModal.emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      setResendModal((prev) => ({ ...prev, feedback: t('orders.emailRequired') }));
      return;
    }
    setResendModal((prev) => ({ ...prev, sending: true, feedback: '' }));
    try {
      await api.post(`/api/orders/${resendModal.orderId}/resend`, { emails });
      setResendModal((prev) => ({ ...prev, sending: false, feedback: 'success' }));
      setTimeout(closeResendModal, 1500);
    } catch (err) {
      console.error('Failed to resend order email:', err);
      setResendModal((prev) => ({ ...prev, sending: false, feedback: err.response?.data?.error || t('orders.resendFailed') }));
    }
  };

  const handleHeaderActionKeyDown = (event, action) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">{t('orders.title')}</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {orders.length === 0 ? (
      <Alert variant="info">{t('orders.noOrders')}</Alert>
      ) : (
        <Accordion>
          {orders.map((order, index) => (
            <Accordion.Item eventKey={index.toString()} key={order.id}>
              <Accordion.Header>
                <div className="d-flex justify-content-between w-100 pe-3 align-items-center">
                  <div>
                    <strong>{t('orders.number')}:</strong> {order.externalId}
                  </div>
                  <div>
                    <strong>{t('orders.date')}:</strong> {formatDate(order.createdAt)}
                  </div>
                  <div>{getStatusBadge(order.status)}</div>
                  <div onClick={(e) => e.stopPropagation()} className="d-flex gap-2">
                    <div
                      role="button"
                      tabIndex={0}
                      className="btn btn-outline-secondary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openResendModal(order.id);
                      }}
                      onKeyDown={(e) => handleHeaderActionKeyDown(e, () => openResendModal(order.id))}
                      title="Resend order confirmation"
                    >
                      ✉️ {t('orders.resend')}
                    </div>
                    {order.pdfUrl && (
                      <div
                        role="button"
                        tabIndex={0}
                        className="btn btn-outline-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPDF(order.pdfUrl, order.externalId);
                        }}
                        onKeyDown={(e) => handleHeaderActionKeyDown(e, () => handleDownloadPDF(order.pdfUrl, order.externalId))}
                        title="Download Order Confirmation PDF"
                      >
                        📄 PDF
                      </div>
                    )}
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Card>
                  <Card.Body>
                    <h5>{t('orders.items')}</h5>
                    <Table striped bordered>
                      <thead>
                        <tr>
                          <th>{t('orders.columns.product')}</th>
                          <th>{t('orders.columns.quantity')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item) => (
                          <tr key={item.id}>
                            <td>{item.name}</td>
                            <td>{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {order.notes && (
                      <div className="mt-3">
                        <strong>{t('orders.notes')}</strong>
                        <p>{order.notes}</p>
                      </div>
                    )}

                    {(order.deliveryStreetAddress || order.deliveryCity) && (
                      <div className="mt-3">
                        <strong>{t('orders.deliveryInformation')}</strong>
                        <p className="mb-1">{order.deliveryStreetAddress}</p>
                        <p className="mb-1">{order.deliveryPostalCode} {order.deliveryCity}</p>
                        {order.deliveryDate && (
                          <p className="mb-0"><strong>{t('orders.deliveryDate')}:</strong> {new Date(order.deliveryDate).toLocaleDateString()}</p>
                        )}
                      </div>
                    )}

                    <div className="mt-3 d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        onClick={() => openResendModal(order.id)}
                      >
                        ✉️ {t('orders.resendConfirmation')}
                      </Button>
                      {order.pdfUrl && (
                        <Button 
                          variant="primary" 
                          onClick={() => handleDownloadPDF(order.pdfUrl, order.externalId)}
                        >
                          📥 {t('orders.downloadPdf')}
                        </Button>
                      )}
                    </div>

                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
      <Modal show={resendModal.show} onHide={closeResendModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>✉️ {t('orders.resendTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t('orders.emailAddresses')}</Form.Label>
            <Form.Control
              type="text"
              placeholder="email@example.com, another@example.com"
              value={resendModal.emails}
              onChange={(e) => setResendModal((prev) => ({ ...prev, emails: e.target.value }))}
              disabled={resendModal.sending}
              autoFocus
            />
            <Form.Text className="text-muted">{t('orders.emailSeparatorHint')}</Form.Text>
          </Form.Group>
          {resendModal.feedback && resendModal.feedback !== 'success' && (
            <Alert variant="danger" className="mt-3 mb-0">{resendModal.feedback}</Alert>
          )}
          {resendModal.feedback === 'success' && (
            <Alert variant="success" className="mt-3 mb-0">{t('orders.resendSuccess')}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeResendModal} disabled={resendModal.sending}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleResendEmail} disabled={resendModal.sending}>
            {resendModal.sending ? t('orders.sending') : t('orders.send')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Orders;
